import { z } from "zod";

/** One selectable option. `value` is what is stored; `label` is what the applicant reads. */
export const optionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export type ChoiceOption = z.infer<typeof optionSchema>;

/**
 * Restricts an answer to the configured option values. When a form has no options configured yet
 * (a half-built draft), any string is accepted rather than everything being rejected — draft
 * validation is what tells the author the field is incomplete, not a wall of applicant-facing
 * errors on a form that is not published anyway.
 *
 * `allowOther` widens this to any non-empty string — for a reference list that is necessarily
 * incomplete or goes stale (LGAs split or get renamed; a course of study we never anticipated),
 * rejecting a real, honestly-typed answer because our own list has a gap is the wrong failure mode.
 */
export const oneOfOptions = (options: ChoiceOption[], allowOther = false): z.ZodTypeAny => {
  if (options.length === 0) return z.string();
  if (allowOther) return z.string();
  const allowed = options.map((option) => option.value);
  return z
    .string()
    .refine((value) => allowed.includes(value), "Select one of the available options");
};
